import TransformedImage from "@/shared/components/TransformedImage";
import { useAppSelector } from "@app/hooks";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import PDFPreview from "@survey-preview/PDFPreview";
import {
  selectMediaFiles,
  selectSurveyConfig,
  selectVisibleProperties,
} from "@survey/surveySelectors";
// Removed unused react-pdf imports after refactor
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

interface PropertySitePlanPreviewProps {
  mode?: string;
  propertyFilter?: string; // Optional filter for specific property ID
}

function PropertySitePlanPreview({
  propertyFilter,
}: PropertySitePlanPreviewProps) {
  // Use store-first architecture with selectors
  const surveyConfig = useAppSelector(selectSurveyConfig);
  const allVisibleProperties = useAppSelector(selectVisibleProperties);
  const mediaFiles = useAppSelector(selectMediaFiles);

  // Filter properties if propertyFilter is provided
  const visibleProperties = propertyFilter
    ? allVisibleProperties.filter((prop) => prop.propertyId === propertyFilter)
    : allVisibleProperties;

  if (!surveyConfig.isIncludeSitePlans || !mediaFiles) return null;

  // Collect all site plans for all visible properties
  const allSitePlans: Array<{
    cloudfrontUrl: string;
    thumbnailUrl?: string;
    mediaId: string;
    isPdf: boolean;
    origin?: string;
  }> = [];
  visibleProperties.forEach((property) => {
    const propertyMedia = mediaFiles[property.propertyId];
    if (!propertyMedia?.floorPlans) return;
    const visibleSitePlans = propertyMedia.floorPlans.filter(
      (sitePlan) => !sitePlan.isHidden && sitePlan.cloudfrontUrl,
    );
    if (visibleSitePlans.length === 0) return;
    visibleSitePlans.forEach((sitePlan) => {
      const isPdf =
        sitePlan.displayName?.toLowerCase().includes(".pdf") || false;
      allSitePlans.push({
        cloudfrontUrl: sitePlan.cloudfrontUrl!,
        thumbnailUrl: sitePlan.thumbnailUrl,
        mediaId: sitePlan.mediaId,
        isPdf,
        origin: sitePlan.origin,
      });
    });
  });

  if (allSitePlans.length === 0) return null;

  const mode = "SitePlanPreview";

  return (
    <>
      {allSitePlans.map((sitePlan, index) =>
        sitePlan.isPdf ? (
          <PDFPreview
            items={[
              {
                cloudfrontUrl: sitePlan.cloudfrontUrl,
                thumbnailUrl: sitePlan.thumbnailUrl,
                mediaId: sitePlan.mediaId,
              },
            ]}
            pdfPaperSize={surveyConfig.pdfPaperSize}
            key={`siteplan-${sitePlan.mediaId || index}`}
          />
        ) : (
          <SurveyPDFPage
            mode={mode}
            key={`siteplan-img-${sitePlan.mediaId || index}`}
          >
            <div className="flex items-center justify-center w-full p-4">
              <TransformedImage
                imageUrl={sitePlan.cloudfrontUrl}
                alt={`Site Plan ${index + 1}`}
                className="max-w-[95%] w-full h-full bg-contain"
                showFallback={true}
                asBackground={true}
              />
            </div>
          </SurveyPDFPage>
        ),
      )}
    </>
  );
}

export default PropertySitePlanPreview;
